import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Search,
  Award,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ShieldOff,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { useParticipants } from '@/hooks/useParticipants';
import { useBadgesDropdown } from '@/hooks/useBadges';
import {
  useParticipantBadges,
  useAssignBadge,
  useRevokeBadge,
} from '@/hooks/useParticipantBadges';
import { ApiConflictError } from '@/lib/api';
import { BADGE_UNIVERSO_COLORS } from 'shared';
import type { Participant } from 'shared';
import type { ParticipantBadge } from '@/hooks/useParticipantBadges';

// ─── Props ────────────────────────────────────────────────────────────────────

interface AssignBadgeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// ─── Active badge row ─────────────────────────────────────────────────────────

interface ActiveBadgeRowProps {
  pb: ParticipantBadge;
  participantId: number;
  onRevoked: (msg: string) => void;
  onError: (msg: string) => void;
}

function ActiveBadgeRow({ pb, participantId, onRevoked, onError }: ActiveBadgeRowProps) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const revoke = useRevokeBadge();

  const handleRevoke = () => {
    revoke.mutate(
      {
        participantId,
        badgeId: pb.badgeId,
        data: reason.trim() ? { reason: reason.trim() } : undefined,
      },
      {
        onSuccess: () => {
          setConfirming(false);
          setReason('');
          onRevoked(`Badge "${pb.badgeName}" revogado com sucesso.`);
        },
        onError: (err: Error) => {
          onError(err.message || 'Erro ao revogar badge.');
        },
      },
    );
  };

  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Award className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{pb.badgeName}</p>
              {pb.badgeUniverso && (
                <span
                  className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                  style={{
                    backgroundColor: BADGE_UNIVERSO_COLORS[pb.badgeUniverso].bg,
                    color: BADGE_UNIVERSO_COLORS[pb.badgeUniverso].text,
                  }}
                >
                  {pb.badgeUniverso}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Atribuído em {formatDate(pb.assignedAt)} por {pb.assignedByName}
            </p>
            {pb.description && (
              <p className="text-xs text-muted-foreground italic mt-0.5">"{pb.description}"</p>
            )}
          </div>
        </div>
        {!confirming && (
          <button
            onClick={() => setConfirming(true)}
            className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors px-2 py-1 rounded hover:bg-destructive/10"
            title="Revogar badge"
          >
            <ShieldOff className="w-3.5 h-3.5" />
            Revogar
          </button>
        )}
      </div>

      {/* Inline revoke confirmation */}
      {confirming && (
        <div className="border-t border-border pt-2 space-y-2">
          <p className="text-xs text-muted-foreground">
            Motivo da revogação <span className="text-muted-foreground/60">(opcional)</span>
          </p>
          <input
            type="text"
            className="input text-sm py-1.5"
            placeholder="ex: Badge expirado, transferência…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
          />
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setConfirming(false); setReason(''); }}
              className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleRevoke}
              disabled={revoke.isPending}
              className="flex items-center gap-1 text-xs text-destructive-foreground bg-destructive hover:bg-destructive/90 px-3 py-1.5 rounded transition-colors disabled:opacity-50"
            >
              {revoke.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
              Confirmar revogação
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function AssignBadgeModal({ isOpen, onClose }: AssignBadgeModalProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // Assign form
  const [selectedBadgeId, setSelectedBadgeId] = useState('');
  const [description, setDescription] = useState('');

  // Toast feedback
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const assign = useAssignBadge();

  // Search participants
  const { data: participantsData, isLoading: participantsLoading } = useParticipants({
    limit: 8,
    offset: 0,
    search: debouncedSearch,
  });

  // Active badges list for selected participant
  const {
    data: badgesData,
    isLoading: badgesLoading,
  } = useParticipantBadges(selectedParticipant?.id ?? null);

  // Badge dropdown options
  const { data: availableBadges, isLoading: badgesDropdownLoading } = useBadgesDropdown();

  const activeBadges = (badgesData?.badges ?? []).filter((b) => !b.revokedAt);

  // Badges the participant doesn't already have active (for the dropdown)
  const assignableBadges = (availableBadges ?? []).filter(
    (b) => !activeBadges.some((pb) => pb.badgeId === b.id),
  );

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setDebouncedSearch('');
      setSelectedParticipant(null);
      setShowDropdown(false);
      setSelectedBadgeId('');
      setDescription('');
      setToast(null);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Dismiss toast after 4 s
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setSelectedParticipant(null);
    setShowDropdown(value.length >= 2);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, []);

  const handleSelectParticipant = (p: Participant) => {
    setSelectedParticipant(p);
    setSearch(`${p.nome} ${p.apelido}`);
    setShowDropdown(false);
    setSelectedBadgeId('');
    setDescription('');
    setToast(null);
  };

  const handleAssign = () => {
    if (!selectedParticipant || !selectedBadgeId) return;

    assign.mutate(
      {
        participantId: selectedParticipant.id,
        data: {
          badgeId: selectedBadgeId,
          description: description.trim() || null,
        },
      },
      {
        onSuccess: (pb) => {
          setSelectedBadgeId('');
          setDescription('');
          setToast({ type: 'success', message: `Badge "${pb.badgeName}" atribuído com sucesso.` });
        },
        onError: (err: Error) => {
          if (err instanceof ApiConflictError && err.code === 'BADGE_ALREADY_ASSIGNED') {
            setToast({ type: 'error', message: 'Este badge já está atribuído a este participante.' });
          } else {
            setToast({ type: 'error', message: err.message || 'Erro ao atribuir badge.' });
          }
        },
      },
    );
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  const participants = participantsData?.participants ?? [];
  const descLen = description.length;
  const canAssign = !!selectedParticipant && !!selectedBadgeId && !assign.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-badge-modal-title"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            <h2 id="assign-badge-modal-title" className="text-lg font-semibold text-foreground">
              Atribuir Badge a Utilizador
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Toast */}
          {toast && (
            <div
              className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium ${
                toast.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                  : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
              }`}
            >
              {toast.type === 'success'
                ? <CheckCircle2 className="w-4 h-4 shrink-0" />
                : <AlertCircle className="w-4 h-4 shrink-0" />}
              {toast.message}
            </div>
          )}

          {/* ── Step 1: Participant search ── */}
          <div>
            <label className="label mb-1.5 block" htmlFor="participant-search">
              Pesquisar participante
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={searchRef}
                id="participant-search"
                type="text"
                className="input pl-9"
                placeholder="Nome, email, telemóvel ou número…"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                autoComplete="off"
              />
            </div>

            {/* Results dropdown */}
            {showDropdown && (
              <div className="mt-1 border border-border rounded-md bg-card shadow-md max-h-48 overflow-y-auto">
                {participantsLoading ? (
                  <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> A pesquisar…
                  </div>
                ) : participants.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-muted-foreground">Sem resultados.</div>
                ) : (
                  participants.map((p) => (
                    <button
                      key={p.id}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-accent text-left transition-colors"
                      onClick={() => handleSelectParticipant(p)}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                        {p.nome.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {p.nome} {p.apelido}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {[p.email, p.telemovel, p.numeroCc].filter(Boolean).join(' · ')}
                          {!p.email && !p.telemovel && !p.numeroCc && `#${p.id}`}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0 tabular-nums">#{p.id}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* ── Step 2: Participant selected — show badges + assign form ── */}
          {selectedParticipant && (
            <>
              {/* Selected participant card */}
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary/5 border border-primary/20">
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                  {selectedParticipant.nome.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {selectedParticipant.nome} {selectedParticipant.apelido}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    #{selectedParticipant.id}
                    {selectedParticipant.email && ` · ${selectedParticipant.email}`}
                  </p>
                </div>
              </div>

              {/* Current active badges */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Badges activos
                </h3>
                {badgesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
                  </div>
                ) : activeBadges.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    Este participante ainda não tem badges activos.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {activeBadges.map((pb) => (
                      <ActiveBadgeRow
                        key={pb.id}
                        pb={pb}
                        participantId={selectedParticipant.id}
                        onRevoked={(msg) => setToast({ type: 'success', message: msg })}
                        onError={(msg) => setToast({ type: 'error', message: msg })}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Assign new badge form */}
              <div className="border-t border-border pt-4 space-y-3">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" />
                  Atribuir novo badge
                </h3>

                {/* Badge select */}
                <div>
                  <label className="label mb-1.5 block text-sm" htmlFor="badge-select">
                    Badge <span className="text-destructive">*</span>
                  </label>
                  {badgesDropdownLoading ? (
                    <div className="input flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
                    </div>
                  ) : assignableBadges.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {availableBadges?.length === 0
                        ? 'Não existem badges activos.'
                        : 'Este participante já possui todos os badges activos.'}
                    </p>
                  ) : (
                    <div className="relative">
                      <select
                        id="badge-select"
                        className="input appearance-none pr-8"
                        value={selectedBadgeId}
                        onChange={(e) => setSelectedBadgeId(e.target.value)}
                      >
                        <option value="">Selecionar badge…</option>
                        {assignableBadges.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="label text-sm" htmlFor="badge-description">
                      Descrição
                      <span className="text-muted-foreground font-normal"> (opcional)</span>
                    </label>
                    <span
                      className={`text-xs tabular-nums ${
                        descLen > 180
                          ? 'text-destructive'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {descLen}/200
                    </span>
                  </div>
                  <textarea
                    id="badge-description"
                    className="input resize-none text-sm"
                    rows={3}
                    placeholder="Motivo da atribuição ou observações…"
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, 200))}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button onClick={onClose} className="btn-outline text-sm">
            Fechar
          </button>
          {selectedParticipant && (
            <button
              onClick={handleAssign}
              disabled={!canAssign}
              className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assign.isPending
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Award className="w-4 h-4" />}
              {assign.isPending ? 'A atribuir…' : 'Atribuir Badge'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
